import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Copy, Check, Save, Building2, Wallet, ShieldCheck, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import CurrencyIcon from './CurrencyIcon';

const CopyField = ({ label, value, testId }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => { navigator.clipboard.writeText(value); setCopied(true); toast.success('Copié !'); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 border border-gray-200">
      <div className="min-w-0">
        <div className="text-[10px] uppercase font-bold text-gray-400">{label}</div>
        <div className="text-sm font-mono-data text-gray-900 truncate">{value}</div>
      </div>
      <button onClick={copy} className="p-2 hover:bg-gray-100 rounded-full shrink-0 transition-colors" data-testid={testId}>
        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
      </button>
    </div>
  );
};

const STATUS_LABEL = {
  open: 'Ouvert', matched: 'Contrepartie trouvée', escrow_pending: 'Séquestre en attente',
  escrow_received: 'Séquestre reçu', fiat_sent: 'Paiement fiat envoyé', completed: 'Terminé',
  cancelled: 'Annulé', rejected: 'Refusé',
};

export const AdminP2PDetail = ({ order, onClose, onRefresh }) => {
  const [amount, setAmount] = useState(String(order.amount_crypto));
  const [price, setPrice] = useState(String(order.price_per_unit));
  const [note, setNote] = useState(order.admin_note || '');
  const [walletForSeller, setWalletForSeller] = useState(order.admin_wallet_for_seller || '');
  const [cardForBuyer, setCardForBuyer] = useState(order.admin_card_for_buyer || '');
  const [publicNote, setPublicNote] = useState(order.admin_public_note || '');
  const [busy, setBusy] = useState(false);

  const sellerIsMaker = order.side === 'sell';
  const seller = {
    email: sellerIsMaker ? order.maker_email : order.taker_email,
    bank: sellerIsMaker ? order.bank_name : order.taker_bank_name,
    card: sellerIsMaker ? order.bank_card : order.taker_bank_card,
  };
  const buyer = {
    email: sellerIsMaker ? order.taker_email : order.maker_email,
    wallet: sellerIsMaker ? order.taker_wallet_address : order.wallet_address,
  };

  const fiat = (parseFloat(amount) || 0) * (parseFloat(price) || 0);
  const commission = (parseFloat(amount) || 0) * 0.07;
  const buyerGets = (parseFloat(amount) || 0) - commission;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); onRefresh(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const saveEdits = () => act(
    () => api.put(`/admin/p2p/orders/${order.id}`, {
      amount_crypto: parseFloat(amount), price_per_unit: parseFloat(price), admin_note: note,
      admin_wallet_for_seller: walletForSeller, admin_card_for_buyer: cardForBuyer, admin_public_note: publicNote,
    }),
    'Modifications enregistrées ✓'
  );
  const setStatus = (status, msg) => act(() => api.put(`/admin/p2p/orders/${order.id}/status`, { status }), msg);
  const companyMatch = () => act(() => api.post(`/admin/p2p/orders/${order.id}/company-match`), 'alphaswapp est maintenant la contrepartie ✓');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="p2p-detail-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-black rounded-t-3xl p-6 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center space-x-3">
            <CurrencyIcon symbol={order.crypto} size={36} />
            <div>
              <div className="text-white font-extrabold font-mono-data">
                {order.order_number ? `N° ${order.order_number} · ` : ''}{order.side === 'sell' ? 'VENTE' : 'ACHAT'} · {order.amount_crypto} {order.crypto} → {order.amount_fiat?.toFixed(2)} {order.fiat_currency}
              </div>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-white/15 text-white/80">{STATUS_LABEL[order.status] || order.status}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${order.counterparty_type === 'company' ? 'gradient-pink-violet text-white' : 'bg-emerald-400/20 text-emerald-300'}`}>
                  {order.counterparty_type === 'company' ? 'Contrepartie : alphaswapp' : order.taker_email ? 'P2P · entre clients' : 'En attente de contrepartie'}
                </span>
                {order.refund_required && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-rose-400/20 text-rose-300">Remboursement requis</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-testid="close-detail-btn">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Parties */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-rose-50/60 border border-rose-100 rounded-2xl p-4 space-y-2">
              <div className="flex items-center space-x-2 mb-1">
                <Building2 className="w-4 h-4 text-rose-500" />
                <span className="text-sm font-extrabold text-gray-900">Vendeur {sellerIsMaker ? '(maker)' : '(taker)'}</span>
              </div>
              <CopyField label="Email" value={seller.email} testId="copy-seller-email" />
              <CopyField label="Banque" value={seller.bank} testId="copy-seller-bank" />
              <CopyField label="Carte bancaire" value={seller.card} testId="copy-seller-card" />
              {!seller.email && <div className="text-xs text-gray-400 italic">Pas encore de vendeur</div>}
            </div>
            <div className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-4 space-y-2">
              <div className="flex items-center space-x-2 mb-1">
                <Wallet className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-extrabold text-gray-900">Acheteur {sellerIsMaker ? '(taker)' : '(maker)'}</span>
              </div>
              <CopyField label="Email" value={buyer.email} testId="copy-buyer-email" />
              <CopyField label={`Wallet (${order.network})`} value={buyer.wallet} testId="copy-buyer-wallet" />
              {!buyer.email && <div className="text-xs text-gray-400 italic">Pas encore d'acheteur</div>}
            </div>
          </div>

          {/* Distribution des informations */}
          <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-4">
            <div className="text-sm font-extrabold text-gray-900 mb-1">Distribution des informations aux parties</div>
            <p className="text-xs text-gray-500 mb-3">Les clients ne voient JAMAIS les coordonnées de l'autre partie. Ce que vous saisissez ici est affiché dans leurs ordres respectifs.</p>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500">Wallet communiqué au VENDEUR (où envoyer la crypto)</label>
                <input type="text" value={walletForSeller} onChange={(e) => setWalletForSeller(e.target.value)} placeholder="bc1q... / adresse de réception"
                  className="w-full bg-white rounded-xl px-4 py-2.5 mt-1 outline-none font-mono-data text-sm border border-gray-200 focus:border-gray-900" data-testid="admin-wallet-for-seller" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500">Carte communiquée à l'ACHETEUR (où envoyer le paiement)</label>
                <input type="text" value={cardForBuyer} onChange={(e) => setCardForBuyer(e.target.value)} placeholder="Numéro de carte / IBAN + banque"
                  className="w-full bg-white rounded-xl px-4 py-2.5 mt-1 outline-none font-mono-data text-sm border border-gray-200 focus:border-gray-900" data-testid="admin-card-for-buyer" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500">Message visible par les deux parties (facultatif)</label>
                <input type="text" value={publicNote} onChange={(e) => setPublicNote(e.target.value)} placeholder="Ex : Vérification en cours, paiement sous 2h..."
                  className="w-full bg-white rounded-xl px-4 py-2.5 mt-1 outline-none text-sm border border-gray-200 focus:border-gray-900" data-testid="admin-public-note" />
              </div>
            </div>
          </div>

          {/* Editable amounts */}
          <div className="bg-[#F5F6F7] rounded-2xl p-4">
            <div className="text-sm font-extrabold text-gray-900 mb-3">Affinage des montants</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-400">Montant ({order.crypto})</label>
                <input type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-white rounded-xl px-4 py-2.5 mt-1 outline-none font-mono-data border border-gray-200 focus:border-gray-900" data-testid="edit-amount-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-400">Prix unitaire ({order.fiat_currency})</label>
                <input type="number" step="any" value={price} onChange={(e) => setPrice(e.target.value)}
                  className="w-full bg-white rounded-xl px-4 py-2.5 mt-1 outline-none font-mono-data border border-gray-200 focus:border-gray-900" data-testid="edit-price-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white rounded-xl p-2.5"><div className="text-[10px] text-gray-400 uppercase font-bold">Total fiat</div><div className="text-sm font-extrabold text-gray-900 font-mono-data">{fiat.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</div></div>
              <div className="bg-white rounded-xl p-2.5"><div className="text-[10px] text-gray-400 uppercase font-bold">Commission 7%</div><div className="text-sm font-extrabold text-rose-600 font-mono-data">{commission.toFixed(6)}</div></div>
              <div className="bg-white rounded-xl p-2.5"><div className="text-[10px] text-gray-400 uppercase font-bold">Acheteur reçoit</div><div className="text-sm font-extrabold text-emerald-600 font-mono-data">{buyerGets.toFixed(6)}</div></div>
            </div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note interne admin..."
              className="w-full bg-white rounded-xl px-4 py-2.5 mt-3 outline-none text-sm border border-gray-200 resize-none" data-testid="admin-note-input" />
            <button onClick={saveEdits} disabled={busy} className="btn-pill-dark px-5 py-2.5 text-sm mt-3 flex items-center space-x-2 disabled:opacity-50" data-testid="save-edits-btn">
              <Save className="w-4 h-4" /><span>Enregistrer les modifications</span>
            </button>
          </div>

          {/* Actions */}
          <div>
            <div className="text-sm font-extrabold text-gray-900 mb-3">Actions</div>
            <div className="flex flex-wrap gap-2">
              {order.status === 'open' && (
                <button onClick={companyMatch} disabled={busy} className="gradient-pink-violet text-white rounded-full px-5 py-2.5 text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-50" data-testid="company-match-btn">
                  ⚡ alphaswapp prend cet ordre
                </button>
              )}
              {['matched', 'escrow_pending'].includes(order.status) && (
                <button onClick={() => setStatus('escrow_received', 'Séquestre confirmé ✓')} disabled={busy} className="btn-pill-dark px-5 py-2.5 text-sm disabled:opacity-50" data-testid="detail-escrow-btn">
                  <ShieldCheck className="w-4 h-4 inline mr-1.5" />Séquestre reçu ✓
                </button>
              )}
              {['escrow_received', 'fiat_sent'].includes(order.status) && (
                <button onClick={() => setStatus('completed', 'Crypto libérée ✓')} disabled={busy} className="bg-emerald-600 text-white rounded-full px-5 py-2.5 text-sm font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="detail-complete-btn">
                  Libérer la crypto ✓
                </button>
              )}
              {!['completed', 'cancelled', 'rejected'].includes(order.status) && (
                <>
                  <button onClick={() => setStatus('rejected', 'Transaction refusée')} disabled={busy} className="bg-rose-100 text-rose-700 rounded-full px-5 py-2.5 text-sm font-bold hover:bg-rose-200 transition-colors disabled:opacity-50" data-testid="detail-reject-btn">
                    Refuser
                  </button>
                  <button onClick={() => setStatus('cancelled', 'Annulé — pensez au remboursement si séquestre reçu')} disabled={busy} className="border border-gray-300 text-gray-600 rounded-full px-5 py-2.5 text-sm font-bold hover:bg-gray-100 transition-colors disabled:opacity-50" data-testid="detail-cancel-btn">
                    Annuler (retour des fonds)
                  </button>
                </>
              )}
            </div>
            {['escrow_pending', 'escrow_received', 'fiat_sent'].includes(order.status) && (
              <div className="flex items-start space-x-2 mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Des fonds sont en séquestre. En cas d'annulation ou de refus, effectuez le remboursement manuellement vers le wallet/compte d'origine.</span>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminP2PDetail;
